import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Skeleton } from '../components/ui/Skeleton';
import PortfolioModal from '../components/PortfolioModal';
import ScheduleCalendar from '../components/ScheduleCalendar';
import { cn, formatCurrency, fmtDate } from '../lib/utils';
import toast from 'react-hot-toast';
import * as Lu from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useRole } from '../hooks/useRole';
import { useStudentAccess } from '../hooks/useStudentAccess';

const DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ATT_LIMIT_OPTIONS = [3, 5, 10, 15, 30, 50];

const ATT_TYPE_STYLES = {
  regular: { label: 'Regular', variant: 'default' },
  makeup: { label: 'Makeup', variant: 'primary' },
  extra: { label: 'Extra', variant: 'warning' },
  absent: { label: 'Absent', variant: 'danger' },
  no_class: { label: 'No Class', variant: 'danger' },
};

const STATUS_STYLES = {
  present: { label: 'Present', variant: 'success' },
  absent: { label: 'Absent', variant: 'danger' },
};

const CollapsibleSection = ({ title, icon, children, count, defaultOpen = true, primaryAction }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-300">
            <SafeIcon icon={icon} size={16} />
          </div>
          <h2 className="text-xs font-bold dark:text-white flex items-center gap-2">
            {title}
            {count !== undefined && (
              <span className="bg-primary-50 text-primary-600 px-2 py-0.5 rounded-full text-[9px]">{count}</span>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {primaryAction && <div onClick={e => e.stopPropagation()}>{primaryAction}</div>}
          <SafeIcon icon={isOpen ? Lu.LuChevronUp : Lu.LuChevronDown} className="text-slate-400" size={16} />
        </div>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4 pt-0 border-t border-slate-50 dark:border-slate-800">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile: currentProfile, isAdmin, loading: roleLoading } = useRole();
  const { hasAccess, loading: accessLoading } = useStudentAccess(id);

  const [data, setData] = useState({
    student: null, enrollments: [], attendance: [],
    subjects: [], guardians: [], teachers: [], loading: true
  });

  const [ui, setUi] = useState({
    editing: false, adjustOpen: false, enrollOpen: false,
    selected: null, portfolioOpen: false, selectedAttendance: null,
    selectedPortfolio: null, calActionOpen: false, calActionData: null,
    guardianModal: false, guardianEditId: null, editAttOpen: false, editAttRecord: null,
  });

  const [form, setForm] = useState({
    profile: {},
    adjust: {
      rate: '', duration: '', frequency: '', days: [],
      enrollment_start_date: '', enrollment_end_date: '',
      main_teacher_id: '', relief_teacher_ids: [],
    },
    guardian: { label: 'Mother', name: '', phone: '', email: '' },
    enroll: { subject_id: '' },
    attAction: { status: 'present', type: 'regular', time: '14:00', makeup_for: '' },
    editAtt: { status: 'present', attendance_type: 'regular', date: '', time: '', makeup_for_date: '' },
  });

  const [attLimit, setAttLimit] = useState(5);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [downloadingPass, setDownloadingPass] = useState(false);

  const load = async () => {
    try {
      const { data: st } = await supabase.from('students').select('*').eq('id', id).single();
      const [en, sub, att, gd, teachers] = await Promise.all([
        supabase.from('student_subjects').select('*,subjects(*)').eq('student_id', id),
        supabase.from('subjects').select('*').order('name'),
        supabase.from('attendance').select('*,subjects(*),student_portfolios(*)').eq('student_id', id).order('timestamp', { ascending: false }),
        supabase.from('guardians').select('*').eq('student_id', id).order('created_at'),
        supabase.from('profiles').select('id, first_name, last_name, teacher_nickname, role').order('first_name'),
      ]);
      setData({
        student: st,
        enrollments: en.data || [],
        subjects: sub.data || [],
        attendance: att.data || [],
        guardians: gd.data || [],
        teachers: teachers.data || [],
        loading: false,
      });
      setForm(p => ({ ...p, profile: { ...st } }));
    } catch (e) {
      toast.error('Load failed');
    }
  };

  useEffect(() => { load(); }, [id]);

  const getTeacherName = (teacher) => {
    if (!teacher) return '';
    return teacher.teacher_nickname ||
      `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() ||
      teacher.email || 'Unknown';
  };

  const getTeacherById = (tid) => data.teachers.find(t => t.id === tid);

  const saveProfile = async () => {
    const l = toast.loading('Saving...');
    try {
      await supabase.from('students').update(form.profile).eq('id', id);
      toast.success('Saved', { id: l });
      setUi(u => ({ ...u, editing: false }));
      load();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const handleDeleteStudent = async () => {
    if (!confirm(`Permanently delete ${data.student?.full_name || 'this student'}?\n\nThis will also remove all their attendance, portfolio, and enrolment records. This cannot be undone.`)) return;
    const l = toast.loading('Deleting student...');
    try {
      const { error } = await supabase.from('students').delete().eq('id', id);
      if (error) throw error;
      toast.success('Student deleted', { id: l });
      navigate('/students');
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!form.enroll.subject_id) return;
    const l = toast.loading('Enrolling...');
    try {
      await supabase.from('student_subjects').insert([{ student_id: id, subject_id: form.enroll.subject_id }]);
      toast.success('Enrolled', { id: l });
      setUi(u => ({ ...u, enrollOpen: false }));
      load();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const deleteEnrollment = async (enrollId) => {
    if (!confirm('Remove this subject enrollment?')) return;
    const l = toast.loading('Removing...');
    try {
      await supabase.from('student_subjects').delete().eq('id', enrollId);
      toast.success('Enrollment removed', { id: l });
      load();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const updateEnrollment = async (e) => {
    e.preventDefault();
    const l = toast.loading('Updating...');
    try {
      await supabase.from('student_subjects').update({
        custom_rate: parseFloat(form.adjust.rate),
        custom_duration: parseFloat(form.adjust.duration),
        custom_frequency: parseInt(form.adjust.frequency),
        scheduled_days: form.adjust.days,
        enrollment_start_date: form.adjust.enrollment_start_date || null,
        enrollment_end_date: form.adjust.enrollment_end_date || null,
        main_teacher_id: form.adjust.main_teacher_id || null,
        relief_teacher_ids: form.adjust.relief_teacher_ids || [],
      }).eq('id', ui.selected.id);
      toast.success('Updated', { id: l });
      setUi(u => ({ ...u, adjustOpen: false }));
      load();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const deleteAttendance = async (attId) => {
    if (!confirm('Delete this attendance record?')) return;
    const l = toast.loading('Deleting...');
    try {
      await supabase.from('attendance').delete().eq('id', attId);
      toast.success('Record deleted', { id: l });
      load();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const openEditAtt = (att) => {
    const d = new Date(att.timestamp);
    setForm(p => ({
      ...p,
      editAtt: {
        status: att.status || 'present',
        attendance_type: att.attendance_type || 'regular',
        date: fmtDate(d, 'yyyy-MM-dd'),
        time: fmtDate(d, 'HH:mm'),
        makeup_for_date: att.makeup_for_date || '',
      }
    }));
    setUi(u => ({ ...u, editAttOpen: true, editAttRecord: att }));
  };

  const submitEditAtt = async (e) => {
    e.preventDefault();
    const l = toast.loading('Updating record...');
    try {
      const timestamp = new Date(`${form.editAtt.date}T${form.editAtt.time}`).toISOString();
      await supabase.from('attendance').update({
        status: form.editAtt.status,
        attendance_type: form.editAtt.attendance_type,
        timestamp,
        makeup_for_date: form.editAtt.attendance_type === 'makeup' ? form.editAtt.makeup_for_date || null : null,
      }).eq('id', ui.editAttRecord.id);
      toast.success('Record updated', { id: l });
      setUi(u => ({ ...u, editAttOpen: false, editAttRecord: null }));
      load();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const handleSaveGuardian = async (e) => {
    e.preventDefault();
    const l = toast.loading('Saving contact...');
    try {
      if (ui.guardianEditId) {
        await supabase.from('guardians').update(form.guardian).eq('id', ui.guardianEditId);
      } else {
        await supabase.from('guardians').insert([{ ...form.guardian, student_id: id }]);
      }
      toast.success('Contact saved', { id: l });
      setUi(u => ({ ...u, guardianModal: false, guardianEditId: null }));
      setForm(p => ({ ...p, guardian: { label: 'Mother', name: '', phone: '', email: '' } }));
      load();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const deleteGuardian = async (gId) => {
    if (!confirm('Remove this contact?')) return;
    await supabase.from('guardians').delete().eq('id', gId);
    load();
  };

  const openAdjust = (en) => {
    setForm(p => ({
      ...p,
      adjust: {
        rate: en.custom_rate ?? en.subjects.rate,
        duration: en.custom_duration ?? en.subjects.duration_hours,
        frequency: en.custom_frequency ?? en.subjects.frequency,
        days: en.scheduled_days || [],
        enrollment_start_date: en.enrollment_start_date || '',
        enrollment_end_date: en.enrollment_end_date || '',
        main_teacher_id: en.main_teacher_id || '',
        relief_teacher_ids: en.relief_teacher_ids || [],
      }
    }));
    setUi(u => ({ ...u, adjustOpen: true, selected: en }));
  };

  const openGuardianEdit = (g) => {
    setForm(p => ({ ...p, guardian: { label: g.label, name: g.name, phone: g.phone, email: g.email } }));
    setUi(u => ({ ...u, guardianModal: true, guardianEditId: g.id }));
  };

  const openPortfolio = (att) => {
    setUi({ ...ui, portfolioOpen: true, selectedAttendance: att });
  };

  // Returns true if the current user may edit/portfolio this attendance record.
  // Admins (owner/executive) always can. Teachers only if they are main or relief
  // teacher for the subject tied to this record.
  const canInteractWithAtt = (att) => {
    if (isAdmin) return true;
    const enrollment = data.enrollments.find(en => en.subject_id === att.subject_id);
    if (!enrollment) return false;
    return (
      enrollment.main_teacher_id === currentProfile?.id ||
      (enrollment.relief_teacher_ids || []).includes(currentProfile?.id)
    );
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File must be smaller than 8 MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploadingPicture(true);
    const l = toast.loading('Uploading photo...');
    try {
      const ext = file.name.split('.').pop();
      const filePath = `students/${id}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('student-photos')
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage
        .from('student-photos')
        .getPublicUrl(filePath);
      setForm(p => ({ ...p, profile: { ...p.profile, profile_picture_url: publicUrl } }));
      toast.success('Photo uploaded!', { id: l });
    } catch (err) {
      toast.error(err.message, { id: l });
    } finally {
      setUploadingPicture(false);
      e.target.value = '';
    }
  };

  const handleCalendarSelect = (date, status, enrollment) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setUi({ ...ui, calActionOpen: true, selected: enrollment, calActionData: { date, dateStr, ...status } });
    setForm(p => ({ ...p, attAction: { status: 'present', type: status.isProjected ? 'regular' : 'extra', time: '14:00', makeup_for: '' } }));
  };

  const submitManualAttendance = async (e) => {
    e.preventDefault();
    const l = toast.loading('Saving attendance...');
    try {
      const timestamp = new Date(`${ui.calActionData.dateStr}T${form.attAction.time}`).toISOString();
      await supabase.from('attendance').insert([{
        student_id: id,
        subject_id: ui.selected.subject_id,
        timestamp,
        status: form.attAction.status,
        attendance_type: form.attAction.type,
        makeup_for_date: form.attAction.type === 'makeup' ? form.attAction.makeup_for : null,
        duration_hours: ui.selected.custom_duration ?? ui.selected.subjects.duration_hours,
      }]);
      toast.success('Attendance updated', { id: l });
      setUi(u => ({ ...u, calActionOpen: false }));
      load();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const toggleDay = (day) => {
    const current = [...form.adjust.days];
    const next = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
    setForm(p => ({ ...p, adjust: { ...p.adjust, days: next } }));
  };

  const toggleReliefTeacher = (tid) => {
    const current = [...(form.adjust.relief_teacher_ids || [])];
    const next = current.includes(tid) ? current.filter(t => t !== tid) : [...current, tid];
    setForm(p => ({ ...p, adjust: { ...p.adjust, relief_teacher_ids: next } }));
  };

  const calculateProjection = (rate, duration, freq, type) => {
    const numRate = parseFloat(rate) || 0;
    const numDur = parseFloat(duration) || 0;
    const numFreq = parseInt(freq) || 0;
    const perLesson = type === 'per_lesson' ? numRate : (numRate * numDur);
    const monthly = perLesson * numFreq * 4;
    return { perLesson, monthly };
  };

  // ── Student Pass helpers ──────────────────────────────────────────────────
  const qrCodeUrl = data.student
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('www.tutordash.agency/' + data.student.student_id_code)}&bgcolor=ffffff&color=0f172a&qzone=2&format=png`
    : '';

  const loadImg = (src) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

  const downloadStudentPass = async () => {
    if (downloadingPass || !data.student) return;
    setDownloadingPass(true);
    const l = toast.loading('Generating student pass…');
    try {
      const SCALE = 2, W = 520, H = 760;
      const canvas = document.createElement('canvas');
      canvas.width = W * SCALE; canvas.height = H * SCALE;
      const ctx = canvas.getContext('2d');
      ctx.scale(SCALE, SCALE);

      // White background
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);

      // Blue header
      const grad = ctx.createLinearGradient(0, 0, W, 0);
      grad.addColorStop(0, '#1e40af'); grad.addColorStop(1, '#2563eb');
      ctx.fillStyle = grad; ctx.fillRect(0, 0, W, 170);

      ctx.fillStyle = '#ffffff'; ctx.font = 'bold 21px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('HappyNestling Tuition', W / 2, 65);
      ctx.font = '12px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.75)';
      ctx.fillText('STUDENT PASS', W / 2, 90);
      ctx.fillText(new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long' }), W / 2, 112);

      // Profile picture / initials circle
      const R = 52, CX = 90, CY = 170;
      ctx.beginPath(); ctx.arc(CX, CY, R + 5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff'; ctx.fill();
      const picImg = data.student.profile_picture_url ? await loadImg(data.student.profile_picture_url) : null;
      if (picImg) {
        ctx.save();
        ctx.beginPath(); ctx.arc(CX, CY, R, 0, Math.PI * 2); ctx.clip();
        ctx.drawImage(picImg, CX - R, CY - R, R * 2, R * 2);
        ctx.restore();
      } else {
        ctx.beginPath(); ctx.arc(CX, CY, R, 0, Math.PI * 2);
        ctx.fillStyle = '#dbeafe'; ctx.fill();
        ctx.fillStyle = '#1d4ed8'; ctx.font = `bold ${Math.round(R * 0.8)}px Arial`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText((data.student.full_name || '?').charAt(0).toUpperCase(), CX, CY);
        ctx.textBaseline = 'alphabetic';
      }

      // QR code
      const qrSize = 130, qrX = W - 50 - qrSize, qrY = 180;
      const qrImg = await loadImg(qrCodeUrl);
      if (qrImg) {
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
        ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1;
        ctx.strokeRect(qrX, qrY, qrSize, qrSize);
      }
      ctx.fillStyle = '#94a3b8'; ctx.font = '9px Arial'; ctx.textAlign = 'center';
      ctx.fillText('Scan for attendance', qrX + qrSize / 2, qrY + qrSize + 14);

      // Student info
      const iX = 40; let iY = 245;
      ctx.fillStyle = '#0f172a'; ctx.font = 'bold 22px Arial'; ctx.textAlign = 'left';
      ctx.fillText(data.student.full_name || '', iX, iY); iY += 26;
      ctx.fillStyle = '#64748b'; ctx.font = '12px monospace';
      ctx.fillText(data.student.student_id_code || '', iX, iY); iY += 22;
      if (data.student.payment_method) {
        ctx.fillStyle = '#94a3b8'; ctx.font = '11px Arial';
        ctx.fillText('Payment: ' + data.student.payment_method, iX, iY); iY += 18;
      }

      // Subjects
      if (data.enrollments.length > 0) {
        iY += 8;
        ctx.fillStyle = '#475569'; ctx.font = 'bold 10px Arial';
        ctx.fillText('ENROLLED SUBJECTS', iX, iY); iY += 15;
        ctx.fillStyle = '#0f172a'; ctx.font = '11px Arial';
        data.enrollments.forEach(en => {
          ctx.fillText(`• ${en.subjects.name}  (${en.subjects.level || ''})`, iX, iY); iY += 16;
        });
      }

      // Divider
      iY += 12;
      ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(iX, iY); ctx.lineTo(W - iX, iY); ctx.stroke();
      iY += 18;

      // Bio
      if (data.student.bio) {
        ctx.fillStyle = '#64748b'; ctx.font = 'italic 11px Arial';
        const maxW = W - iX * 2;
        let line = '';
        for (const word of data.student.bio.split(' ')) {
          const test = line + word + ' ';
          if (ctx.measureText(test).width > maxW && line) {
            ctx.fillText(line, iX, iY); line = word + ' '; iY += 15;
          } else { line = test; }
        }
        if (line.trim()) ctx.fillText(line, iX, iY);
      }

      // Footer
      ctx.fillStyle = '#f8fafc'; ctx.fillRect(0, H - 48, W, 48);
      ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, H - 48); ctx.lineTo(W, H - 48); ctx.stroke();
      ctx.fillStyle = '#94a3b8'; ctx.font = '9px Arial'; ctx.textAlign = 'center';
      ctx.fillText('www.tutordash.agency/' + (data.student.student_id_code || ''), W / 2, H - 18);

      const a = document.createElement('a');
      a.download = `student-pass-${data.student.student_id_code || 'pass'}.png`;
      a.href = canvas.toDataURL('image/png', 1.0);
      a.click();
      toast.success('Student pass downloaded!', { id: l });
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate pass', { id: l });
    } finally {
      setDownloadingPass(false);
    }
  };

  const displayedAttendance = attLimit === 'all'
    ? data.attendance
    : data.attendance.slice(0, attLimit);

  if (data.loading || roleLoading || accessLoading) {
    return <div className="p-10"><Skeleton className="h-96 rounded-2xl" /></div>;
  }

  if (!hasAccess) {
    const isRegistrar = data.student?.registered_by === currentProfile?.id;
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center">
          <SafeIcon icon={Lu.LuShieldOff} size={32} className="text-rose-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-700 dark:text-white">Access Denied</h2>
        <p className="text-sm text-slate-400 font-medium text-center max-w-xs">
          You are not assigned as a teacher for this student.
        </p>
        {isRegistrar && data.student && (
          <div className="mt-2 bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4 text-center max-w-xs space-y-3">
            <p className="text-[10px] font-bold text-amber-700">
              You registered <span className="font-black">{data.student.full_name}</span> but no subject was assigned.
              Assign a subject from the Admin Centre and enrol this student, or delete the record below.
            </p>
            <button
              onClick={handleDeleteStudent}
              className="flex items-center gap-2 mx-auto px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl transition-colors"
            >
              <SafeIcon icon={Lu.LuTrash2} size={14} />
              Delete Student Record
            </button>
          </div>
        )}
        <Button variant="ghost" onClick={() => navigate('/students')}>
          <SafeIcon icon={Lu.LuArrowLeft} className="mr-2" /> Back to Students
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-4 pb-32 px-4">
      <Button variant="ghost" onClick={() => navigate('/students')} className="text-slate-500 font-bold">
        <SafeIcon icon={Lu.LuArrowLeft} className="mr-2" /> All Students
      </Button>

      {/* Hero Card */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-900 rounded-2xl border shadow-sm relative overflow-hidden">
        <div className="h-32 sm:h-40 bg-gradient-to-r from-primary-600/10 via-primary-500/5 to-indigo-500/10 w-full absolute top-0 left-0" />
        <div className="px-6 sm:px-10 pb-8 pt-12 sm:pt-20 relative z-10">
          <div className="flex flex-col xl:flex-row items-end gap-6 w-full">
            {/* Flip card: front = profile photo, back = QR code */}
            <div className="relative shrink-0 pb-6" style={{ perspective: '1000px' }}>
              <div
                onClick={() => !ui.editing && setCardFlipped(f => !f)}
                style={{
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: cardFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                  cursor: ui.editing ? 'default' : 'pointer',
                }}
                className="w-32 h-32 sm:w-44 sm:h-44 relative"
              >
                {/* Front: profile picture */}
                <div
                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                  className={cn(
                    'absolute inset-0 rounded-2xl bg-white dark:bg-slate-800 border-8 border-white dark:border-slate-900 shadow-2xl overflow-hidden flex items-center justify-center',
                    ui.editing && 'ring-4 ring-primary-500'
                  )}
                >
                  {data.student.profile_picture_url
                    ? <img src={data.student.profile_picture_url} className="w-full h-full object-cover" />
                    : <SafeIcon icon={Lu.LuUser} size={56} className="text-slate-200" />}
                </div>
                {/* Back: QR code */}
                <div
                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-700 to-primary-500 border-8 border-white dark:border-slate-900 shadow-2xl overflow-hidden flex flex-col items-center justify-center gap-1.5 p-2"
                >
                  {qrCodeUrl && (
                    <img
                      src={qrCodeUrl.replace('size=200x200', 'size=130x130')}
                      alt="Student QR"
                      className="w-[90px] h-[90px] sm:w-[110px] sm:h-[110px] rounded-lg bg-white p-0.5"
                    />
                  )}
                  <p className="text-[7px] sm:text-[8px] text-white/80 font-mono text-center leading-tight px-1">
                    {data.student.student_id_code}
                  </p>
                </div>
              </div>
              {!ui.editing && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[8px] font-medium text-slate-400 whitespace-nowrap">
                  <SafeIcon icon={cardFlipped ? Lu.LuRotateCcw : Lu.LuQrCode} size={9} />
                  {cardFlipped ? 'flip back' : 'tap for QR'}
                </div>
              )}
            </div>
            <div className="flex-1 w-full space-y-4 pb-2">
              {ui.editing ? (
                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border-2 border-primary-100 shadow-xl space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input label="Student Name" value={form.profile.full_name}
                      onChange={e => setForm(p => ({ ...p, profile: { ...p.profile, full_name: e.target.value } }))} />
                    <Input label="Student ID Code" value={form.profile.student_id_code}
                      onChange={e => setForm(p => ({ ...p, profile: { ...p.profile, student_id_code: e.target.value } }))} />
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500 px-1 block">Payment Method</label>
                      <select
                        className="w-full rounded-xl border bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500"
                        value={form.profile.payment_method || ''}
                        onChange={e => setForm(p => ({ ...p, profile: { ...p.profile, payment_method: e.target.value } }))}
                      >
                        <option value="">— Select —</option>
                        <option value="Cash">Cash</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="E-Wallet">E-Wallet</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="col-span-full space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 px-1 block">Profile Picture</label>
                      <div className="flex items-center gap-3">
                        {form.profile.profile_picture_url && (
                          <img
                            src={form.profile.profile_picture_url}
                            alt="Preview"
                            className="w-14 h-14 rounded-xl object-cover border-2 border-slate-100 shrink-0"
                          />
                        )}
                        <label className={cn(
                          'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-dashed cursor-pointer text-sm font-medium transition-all',
                          uploadingPicture
                            ? 'border-primary-300 bg-primary-50 text-primary-600 cursor-wait'
                            : 'border-slate-200 hover:border-primary-300 hover:bg-primary-50 text-slate-500'
                        )}>
                          <SafeIcon icon={uploadingPicture ? Lu.LuLoader : Lu.LuUpload} size={16} className={uploadingPicture ? 'animate-spin' : ''} />
                          {uploadingPicture ? 'Uploading…' : 'Choose Photo (max 8 MB)'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={uploadingPicture}
                            onChange={handlePictureUpload}
                          />
                        </label>
                      </div>
                      {form.profile.profile_picture_url && (
                        <button
                          type="button"
                          onClick={() => setForm(p => ({ ...p, profile: { ...p.profile, profile_picture_url: '' } }))}
                          className="text-[10px] font-medium text-rose-400 hover:text-rose-600 px-1"
                        >
                          Remove photo
                        </button>
                      )}
                    </div>
                    <div className="col-span-full">
                      <label className="text-[10px] font-bold text-slate-500 mb-2 block px-1">Notes</label>
                      <textarea className="w-full rounded-xl border bg-slate-50 p-4 text-sm outline-none min-h-[80px]"
                        value={form.profile.bio}
                        onChange={e => setForm(p => ({ ...p, profile: { ...p.profile, bio: e.target.value } }))} />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={saveProfile} className="px-8 rounded-xl">Confirm</Button>
                    <Button variant="ghost" onClick={() => setUi({ ...ui, editing: false })}>Discard</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col lg:flex-row justify-between items-end gap-4 w-full">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h1 className="text-3xl sm:text-5xl font-bold dark:text-white leading-tight">
                          {data.student.full_name}
                        </h1>
                        <button onClick={() => setUi({ ...ui, editing: true })} className="p-2 text-slate-300 hover:text-primary-500">
                          <SafeIcon icon={Lu.LuPencil} size={18} />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={handleDeleteStudent}
                            title="Delete this student permanently"
                            className="p-2 text-slate-300 hover:text-rose-500 transition-colors"
                          >
                            <SafeIcon icon={Lu.LuTrash2} size={18} />
                          </button>
                        )}
                        <button
                          onClick={downloadStudentPass}
                          disabled={downloadingPass}
                          title="Download Student Pass as PNG"
                          className="p-2 text-slate-300 hover:text-emerald-500 transition-colors"
                        >
                          <SafeIcon icon={downloadingPass ? Lu.LuLoader : Lu.LuDownload} size={18} className={downloadingPass ? 'animate-spin' : ''} />
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-lg font-mono text-[10px] font-bold text-slate-500 tracking-wide border">
                          {data.student.student_id_code}
                        </span>
                        <Badge variant="success" className="px-3 py-1 rounded-lg border">
                          {data.student.payment_method}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="bg-slate-50/50 dark:bg-slate-800/30 p-4 sm:p-6 rounded-xl border">
                    <p className="text-sm text-slate-600 dark:text-slate-400 font-medium italic leading-relaxed">
                      {data.student.bio || 'No notes yet. Click the edit icon to add notes about this student.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column */}
        <div className="lg:col-span-1 space-y-3">
          <CollapsibleSection title="Guardians" icon={Lu.LuShieldCheck} count={data.guardians.length}
            primaryAction={
              <button onClick={() => {
                setForm(p => ({ ...p, guardian: { label: 'Mother', name: '', phone: '', email: '' } }));
                setUi({ ...ui, guardianModal: true, guardianEditId: null });
              }} className="p-1.5 bg-slate-50 rounded-lg text-primary-600">
                <SafeIcon icon={Lu.LuPlus} size={16} />
              </button>
            }>
            <div className="space-y-2 pt-3">
              {data.guardians.length === 0 && (
                <p className="text-[10px] text-center text-slate-300 font-medium py-4">No contacts added</p>
              )}
              {data.guardians.map(g => (
                <div key={g.id} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 border group relative">
                  <div className="flex justify-between items-start mb-1">
                    <Badge variant="primary" className="text-[8px] uppercase">{g.label}</Badge>
                    <div className="flex gap-1">
                      <button onClick={() => openGuardianEdit(g)} className="p-1 text-slate-400 hover:text-primary-600">
                        <SafeIcon icon={Lu.LuPencil} size={13} />
                      </button>
                      <button onClick={() => deleteGuardian(g.id)} className="p-1 text-slate-400 hover:text-rose-600">
                        <SafeIcon icon={Lu.LuTrash2} size={13} />
                      </button>
                    </div>
                  </div>
                  <h4 className="font-bold text-xs dark:text-white">{g.name || 'Unnamed'}</h4>
                  <div className="mt-2 space-y-0.5">
                    <p className="text-[9px] font-medium text-slate-400 flex items-center gap-1.5">
                      <SafeIcon icon={Lu.LuPhone} size={11} className="text-indigo-400" /> {g.phone}
                    </p>
                    {g.email && (
                      <p className="text-[9px] font-medium text-slate-400 lowercase flex items-center gap-1.5">
                        <SafeIcon icon={Lu.LuMail} size={11} className="text-primary-400" /> {g.email}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Enrollment" icon={Lu.LuBookOpen} count={data.enrollments.length}
            primaryAction={
              <button onClick={() => setUi({ ...ui, enrollOpen: true })}
                className="p-1.5 bg-indigo-50 rounded-lg text-indigo-600">
                <SafeIcon icon={Lu.LuPlus} size={16} />
              </button>
            }>
            <div className="space-y-2 pt-3">
              {data.enrollments.length === 0 && (
                <p className="text-[10px] text-center text-slate-300 font-medium py-4">No subjects enrolled</p>
              )}
              {data.enrollments.map(en => {
                const mainTeacher = getTeacherById(en.main_teacher_id);
                const reliefTeachers = (en.relief_teacher_ids || []).map(tid => getTeacherById(tid)).filter(Boolean);
                return (
                  <div key={en.id} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 border group relative">
                    <button onClick={() => deleteEnrollment(en.id)}
                      className="absolute top-3 right-3 p-1.5 text-slate-300 hover:text-rose-500 transition-all">
                      <SafeIcon icon={Lu.LuTrash2} size={14} />
                    </button>
                    <p className="font-bold text-xs dark:text-white pr-6">{en.subjects.name}</p>
                    <Badge variant="primary" className="mt-1 text-[8px] uppercase">{en.subjects.level}</Badge>
                    <div className="mt-2 flex items-center gap-1.5">
                      <SafeIcon icon={Lu.LuCalendarRange} size={10} className="text-indigo-400 shrink-0" />
                      <p className="text-[9px] font-medium text-slate-400">
                        {en.enrollment_start_date ? fmtDate(en.enrollment_start_date, 'dd MMM yyyy') : '—'}
                        {' → '}
                        {en.enrollment_end_date ? fmtDate(en.enrollment_end_date, 'dd MMM yyyy') : 'Ongoing'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {en.scheduled_days?.map(d => (
                        <Badge key={d} variant="warning" className="text-[8px] uppercase font-black">{d}</Badge>
                      ))}
                    </div>
                    {/* Teacher Assignment Display */}
                    {mainTeacher && (
                      <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700 space-y-1">
                        <div className="flex items-center gap-1.5">
                          <SafeIcon icon={Lu.LuUserCheck} size={10} className="text-primary-500 shrink-0" />
                          <p className="text-[9px] font-bold text-primary-600">Main Teacher</p>
                        </div>
                        <p className="text-[10px] font-bold text-slate-600 dark:text-slate-300 pl-4">
                          {getTeacherName(mainTeacher)}
                        </p>
                        {reliefTeachers.length > 0 && (
                          <>
                            <div className="flex items-center gap-1.5 mt-1">
                              <SafeIcon icon={Lu.LuUsers} size={10} className="text-indigo-400 shrink-0" />
                              <p className="text-[9px] font-bold text-indigo-500">Relief</p>
                            </div>
                            <div className="flex flex-wrap gap-1 pl-4">
                              {reliefTeachers.map((t, i) => (
                                <span key={i} className="text-[8px] font-bold bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-full border border-indigo-100">
                                  {getTeacherName(t)}
                                </span>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    <Button onClick={() => openAdjust(en)} variant="secondary"
                      className="w-full mt-3 text-[10px] py-2 rounded-lg border-2 font-bold">
                      Configure Schedule
                    </Button>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-2 space-y-3">
          <CollapsibleSection
            title="Recent Attendance"
            icon={Lu.LuClock}
            count={data.attendance.length}
            primaryAction={
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-medium text-slate-400">Show</span>
                <select
                  value={attLimit === 'all' ? 'all' : String(attLimit)}
                  onChange={e => setAttLimit(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="text-xs font-medium border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 bg-slate-50 dark:bg-slate-800 outline-none text-slate-600 dark:text-slate-300 cursor-pointer"
                >
                  {ATT_LIMIT_OPTIONS.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                  <option value="all">All</option>
                </select>
              </div>
            }
          >
            <div className="divide-y divide-slate-200 dark:divide-slate-700 max-h-[480px] overflow-y-auto">
              {data.attendance.length === 0 && (
                <p className="text-center text-[10px] font-medium text-slate-400 py-6">No attendance records yet</p>
              )}
              {displayedAttendance.map(att => {
                const statusStyle = STATUS_STYLES[att.status] || STATUS_STYLES.absent;
                const typeStyle = ATT_TYPE_STYLES[att.attendance_type] || ATT_TYPE_STYLES.regular;
                return (
                  <div key={att.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 px-1">
                    <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center border shrink-0',
                      att.status === 'present' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600')}>
                      <SafeIcon icon={att.status === 'present' ? Lu.LuCheckCircle : Lu.LuXCircle} size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 mb-0.5">
                        <p className="font-bold text-xs dark:text-white">{att.subjects?.name}</p>
                        <Badge variant={statusStyle.variant} className="text-[7px] uppercase font-black px-1.5 py-0.5">{statusStyle.label}</Badge>
                        <Badge variant={typeStyle.variant} className="text-[7px] uppercase font-black px-1.5 py-0.5">{typeStyle.label}</Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[9px] text-slate-400 font-medium">
                          {fmtDate(att.timestamp, 'dd MMM yyyy')} • {fmtDate(att.timestamp, 'h:mm a')}
                        </p>
                        {att.makeup_for_date && (
                          <span className="flex items-center gap-1 text-[8px] font-bold text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded-full border border-indigo-100">
                            <SafeIcon icon={Lu.LuCalendarClock} size={8} /> Makeup for {fmtDate(att.makeup_for_date, 'dd MMM')}
                          </span>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const canAct = canInteractWithAtt(att);
                      return (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={canAct ? () => openPortfolio(att) : undefined}
                            disabled={!canAct}
                            title={canAct ? 'Lesson Portfolio' : 'Not your subject'}
                            className={cn(
                              'p-1.5 rounded-lg transition-colors',
                              canAct
                                ? att.student_portfolios?.length > 0
                                  ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                  : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                                : 'bg-slate-100 text-slate-300 cursor-not-allowed opacity-50'
                            )}
                          >
                            <SafeIcon icon={Lu.LuClipboardList} size={14} />
                          </button>
                          <button
                            onClick={canAct ? () => openEditAtt(att) : undefined}
                            disabled={!canAct}
                            title={canAct ? 'Edit record' : 'Not your subject'}
                            className={cn(
                              'p-1.5 rounded-lg transition-colors',
                              canAct
                                ? 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                                : 'bg-slate-100 text-slate-300 cursor-not-allowed opacity-50'
                            )}
                          >
                            <SafeIcon icon={Lu.LuPencil} size={14} />
                          </button>
                          <button
                            onClick={canAct ? () => deleteAttendance(att.id) : undefined}
                            disabled={!canAct}
                            title={canAct ? 'Delete record' : 'Not your subject'}
                            className={cn(
                              'p-1.5 rounded-lg transition-colors',
                              canAct
                                ? 'bg-rose-50 text-rose-600 hover:bg-rose-100'
                                : 'bg-slate-100 text-slate-300 cursor-not-allowed opacity-50'
                            )}
                          >
                            <SafeIcon icon={Lu.LuTrash2} size={14} />
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>

          {/* Academic Projection */}
          <div className="space-y-3">
            <h2 className="text-base font-bold flex items-center gap-2 px-1 dark:text-white">
              <SafeIcon icon={Lu.LuTrendingUp} className="text-primary-600" size={18} /> Academic &amp; Projection
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.enrollments.map(en => (
                <div key={en.id} className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <h4 className="font-bold text-[10px] text-slate-400">{en.subjects.name}</h4>
                    <Badge variant="primary" className="text-[8px] uppercase">{en.subjects.level}</Badge>
                  </div>
                  <ScheduleCalendar
                    enrollment={en}
                    attendance={data.attendance.filter(a => a.subject_id === en.subject_id)}
                    onAction={(date, status) => handleCalendarSelect(date, status, en)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Configure Schedule Modal — with Teacher Assignment */}
      <Modal isOpen={ui.adjustOpen} onClose={() => setUi({ ...ui, adjustOpen: false })}
        title="Configure Schedule" className="max-w-xl" style={{ maxHeight: '90vh' }}>
        {ui.selected && (() => {
          const baseline = calculateProjection(
            ui.selected.subjects.rate, ui.selected.subjects.duration_hours,
            ui.selected.subjects.frequency, ui.selected.subjects.billing_cycle_type
          );
          const custom = calculateProjection(
            form.adjust.rate, form.adjust.duration,
            form.adjust.frequency, ui.selected.subjects.billing_cycle_type
          );
          const availableReliefTeachers = data.teachers.filter(t => t.id !== form.adjust.main_teacher_id);

          return (
            <form onSubmit={updateEnrollment} className="flex flex-col" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              <div className="overflow-y-auto flex-1 px-6 pb-4 space-y-4" style={{ overscrollBehavior: 'contain' }}>

                {/* Projection Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-900 rounded-2xl p-4 text-white">
                    <p className="text-[8px] font-bold text-slate-400 mb-2">Baseline</p>
                    <p className="text-[9px] font-medium text-slate-500">Rate</p>
                    <p className="text-base font-bold">{formatCurrency(ui.selected.subjects.rate)}</p>
                    <p className="text-[9px] font-medium text-slate-500 mt-1">Monthly</p>
                    <p className="text-base font-bold">{formatCurrency(baseline.monthly)}</p>
                  </div>
                  <div className="bg-primary-600 rounded-2xl p-4 text-white shadow-lg shadow-primary-500/20">
                    <p className="text-[8px] font-bold text-white/50 mb-2">Personalized</p>
                    <p className="text-[9px] font-medium text-white/50">Per Session</p>
                    <p className="text-base font-bold">{formatCurrency(custom.perLesson)}</p>
                    <p className="text-[9px] font-medium text-white/50 mt-1">Monthly</p>
                    <p className="text-base font-bold">{formatCurrency(custom.monthly)}</p>
                  </div>
                </div>

                {/* Teacher Assignment */}
                <div className="bg-primary-50 border border-primary-100 rounded-2xl p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={Lu.LuUserCheck} size={14} className="text-primary-600" />
                    <p className="text-[10px] font-bold text-primary-700">Teacher Assignment</p>
                  </div>

                  {/* Main Teacher */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 px-1 block">
                      Main Teacher <span className="text-rose-500">*</span>
                    </label>
                    <select
                      className="w-full bg-white p-3 rounded-xl font-bold text-xs border border-primary-200 outline-none focus:ring-2 focus:ring-primary-400"
                      value={form.adjust.main_teacher_id}
                      onChange={e => {
                        const newMainId = e.target.value;
                        setForm(p => ({
                          ...p,
                          adjust: {
                            ...p.adjust,
                            main_teacher_id: newMainId,
                            relief_teacher_ids: (p.adjust.relief_teacher_ids || []).filter(id => id !== newMainId),
                          }
                        }));
                      }}
                    >
                      <option value="">— Select Main Teacher —</option>
                      {data.teachers.map(t => (
                        <option key={t.id} value={t.id}>{getTeacherName(t)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Relief Teachers */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 px-1 block">
                      Relief Teachers <span className="text-slate-400 normal-case font-semibold">(select multiple)</span>
                    </label>
                    {availableReliefTeachers.length === 0 ? (
                      <p className="text-[9px] text-slate-400 font-medium px-1">
                        {data.teachers.length <= 1 ? 'No other teachers available' : 'Select a main teacher first'}
                      </p>
                    ) : (
                      <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                        {availableReliefTeachers.map(t => {
                          const isSelected = (form.adjust.relief_teacher_ids || []).includes(t.id);
                          return (
                            <label key={t.id}
                              className={cn(
                                'flex items-center gap-3 p-2.5 rounded-xl border-2 cursor-pointer transition-all',
                                isSelected
                                  ? 'border-indigo-400 bg-indigo-50'
                                  : 'border-slate-100 bg-white hover:border-slate-200'
                              )}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleReliefTeacher(t.id)}
                                className="accent-indigo-600 w-4 h-4 shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <p className={cn('text-[10px] font-bold truncate',
                                  isSelected ? 'text-indigo-700' : 'text-slate-700')}>
                                  {getTeacherName(t)}
                                </p>
                                <p className="text-[8px] text-slate-400 font-medium">{t.role}</p>
                              </div>
                              {isSelected && (
                                <SafeIcon icon={Lu.LuCheck} size={12} className="text-indigo-600 shrink-0" />
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {(form.adjust.relief_teacher_ids || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {(form.adjust.relief_teacher_ids || []).map(tid => {
                          const t = getTeacherById(tid);
                          if (!t) return null;
                          return (
                            <span key={tid} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-[8px] font-bold px-2 py-0.5 rounded-full">
                              {getTeacherName(t)}
                              <button type="button" onClick={() => toggleReliefTeacher(tid)} className="hover:text-rose-600">
                                <SafeIcon icon={Lu.LuX} size={8} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Enrollment Period */}
                <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={Lu.LuCalendarRange} size={14} className="text-indigo-500" />
                    <p className="text-[10px] font-bold text-indigo-600">Enrollment Period</p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 px-1 block">
                      Start Date <span className="text-rose-500">*</span>
                    </label>
                    <input type="date"
                      className="w-48 bg-white p-2 rounded-xl font-bold text-xs border border-indigo-200 outline-none focus:ring-2 focus:ring-indigo-400"
                      value={form.adjust.enrollment_start_date}
                      onChange={e => setForm(p => ({ ...p, adjust: { ...p.adjust, enrollment_start_date: e.target.value } }))}
                      required />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 px-1 block">
                      End Date <span className="text-slate-400 normal-case font-semibold">(blank = ongoing)</span>
                    </label>
                    <input type="date"
                      className="w-48 bg-white p-2 rounded-xl font-bold text-xs border border-indigo-200 outline-none focus:ring-2 focus:ring-indigo-400"
                      value={form.adjust.enrollment_end_date}
                      onChange={e => setForm(p => ({ ...p, adjust: { ...p.adjust, enrollment_end_date: e.target.value } }))}
                      min={form.adjust.enrollment_start_date || undefined} />
                  </div>
                </div>

                {/* Rate & Schedule */}
                <div className="bg-slate-50 p-4 rounded-2xl border space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Custom Rate ($)" type="number" step="0.01" value={form.adjust.rate}
                      onChange={e => setForm(p => ({ ...p, adjust: { ...p.adjust, rate: e.target.value } }))} required />
                    <Input label="Duration (Hours)" type="number" step="0.1" value={form.adjust.duration}
                      onChange={e => setForm(p => ({ ...p, adjust: { ...p.adjust, duration: e.target.value } }))} required />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 mb-2 block px-1">Scheduled Days</label>
                    <div className="flex flex-wrap gap-1.5">
                      {DAYS_SHORT.map(day => (
                        <button key={day} type="button" onClick={() => toggleDay(day)}
                          className={cn('px-3 py-2 rounded-xl text-[10px] font-bold border-2 transition-all',
                            form.adjust.days.includes(day)
                              ? 'bg-primary-600 text-white border-primary-600 shadow-md'
                              : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200')}>
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 mb-1 block px-1">Frequency Override</label>
                    <select
                      className="w-full bg-white p-3 rounded-xl font-bold text-xs border outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.adjust.frequency}
                      onChange={e => setForm(p => ({ ...p, adjust: { ...p.adjust, frequency: e.target.value } }))}>
                      {[1, 2, 3, 4, 5, 6, 7].map(f => <option key={f} value={f}>{f}x Weekly Session</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 px-6 py-4 border-t border-slate-100 bg-white rounded-b-2xl shrink-0">
                <Button variant="ghost" type="button" onClick={() => setUi({ ...ui, adjustOpen: false })} className="flex-1 rounded-xl py-3">
                  Cancel
                </Button>
                <Button type="submit" className="flex-[2] rounded-xl py-3 font-bold text-[10px] shadow-lg">
                  Apply Changes
                </Button>
              </div>
            </form>
          );
        })()}
      </Modal>

      {/* Edit Attendance Modal */}
      <Modal isOpen={ui.editAttOpen} onClose={() => setUi(u => ({ ...u, editAttOpen: false, editAttRecord: null }))}
        title="Edit Attendance Record" className="max-w-md">
        {ui.editAttRecord && (
          <form onSubmit={submitEditAtt} className="p-5 space-y-5">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
              <p className="text-[9px] font-medium text-slate-400 mb-1">Editing Session</p>
              <p className="font-bold text-sm">{ui.editAttRecord.subjects?.name}</p>
              <p className="text-[9px] text-slate-400 font-medium mt-0.5">
                {fmtDate(ui.editAttRecord.timestamp, 'EEEE, dd MMM yyyy')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 px-1 block">Presence</label>
                <select className="w-full bg-slate-50 p-3 rounded-xl font-bold text-xs border outline-none focus:ring-2 focus:ring-primary-500"
                  value={form.editAtt.status}
                  onChange={e => setForm(p => ({ ...p, editAtt: { ...p.editAtt, status: e.target.value } }))}>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 px-1 block">Type</label>
                <select className="w-full bg-slate-50 p-3 rounded-xl font-bold text-xs border outline-none focus:ring-2 focus:ring-primary-500"
                  value={form.editAtt.attendance_type}
                  onChange={e => setForm(p => ({ ...p, editAtt: { ...p.editAtt, attendance_type: e.target.value } }))}>
                  <option value="regular">Regular</option>
                  <option value="makeup">Makeup</option>
                  <option value="extra">Extra</option>
                  <option value="absent">Absent</option>
                  <option value="no_class">No Class / Holiday</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input type="date" label="Date" value={form.editAtt.date}
                onChange={e => setForm(p => ({ ...p, editAtt: { ...p.editAtt, date: e.target.value } }))} required />
              <Input type="time" label="Time" value={form.editAtt.time}
                onChange={e => setForm(p => ({ ...p, editAtt: { ...p.editAtt, time: e.target.value } }))} required />
            </div>
            {form.editAtt.attendance_type === 'makeup' && (
              <Input type="date" label="Makeup For (Original Missed Date)" value={form.editAtt.makeup_for_date}
                onChange={e => setForm(p => ({ ...p, editAtt: { ...p.editAtt, makeup_for_date: e.target.value } }))} />
            )}
            <div className="flex gap-3">
              <Button type="button" variant="ghost" onClick={() => setUi(u => ({ ...u, editAttOpen: false }))} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-[2] font-bold text-[10px] shadow-lg">Save Changes</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Calendar Action Modal */}
      <Modal isOpen={ui.calActionOpen} onClose={() => setUi({ ...ui, calActionOpen: false })}
        title="Log Attendance" className="max-w-md">
        {ui.calActionData && (
          <form onSubmit={submitManualAttendance} className="p-5 space-y-5">
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-100 text-center">
              <p className="text-[10px] font-bold text-slate-400 mb-1">Target Date</p>
              <h3 className="text-xl font-bold dark:text-slate-900">{format(ui.calActionData.date, 'EEEE, dd MMM yyyy')}</h3>
              <Badge variant="primary" className="mt-2 text-[8px] uppercase">{ui.selected?.subjects.name}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 px-1 block">Presence</label>
                <select className="w-full bg-slate-50 p-3.5 rounded-xl font-bold text-xs border outline-none"
                  value={form.attAction.status}
                  onChange={e => setForm(p => ({ ...p, attAction: { ...p.attAction, status: e.target.value } }))}>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 px-1 block">Type</label>
                <select className="w-full bg-slate-50 p-3.5 rounded-xl font-bold text-xs border outline-none"
                  value={form.attAction.type}
                  onChange={e => setForm(p => ({ ...p, attAction: { ...p.attAction, type: e.target.value } }))}>
                  <option value="regular">Regular</option>
                  <option value="makeup">Makeup</option>
                  <option value="extra">Extra</option>
                  <option value="absent">Absent</option>
                  <option value="no_class">No Class / Holiday</option>
                </select>
              </div>
            </div>
            <Input type="time" label="Session Time" value={form.attAction.time}
              onChange={e => setForm(p => ({ ...p, attAction: { ...p.attAction, time: e.target.value } }))} />
            {form.attAction.type === 'makeup' && (
              <Input type="date" label="Makeup For (Original Missed Date)" value={form.attAction.makeup_for}
                onChange={e => setForm(p => ({ ...p, attAction: { ...p.attAction, makeup_for: e.target.value } }))} />
            )}
            <div className="flex gap-3">
              <Button type="button" variant="ghost" onClick={() => setUi({ ...ui, calActionOpen: false })} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-[2] font-bold text-[10px] shadow-lg">Confirm &amp; Save</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Enroll Subject Modal */}
      <Modal isOpen={ui.enrollOpen} onClose={() => setUi({ ...ui, enrollOpen: false })} title="Enroll Subject" className="max-w-md">
        <form onSubmit={handleEnroll} className="p-5 space-y-5">
          <select className="w-full bg-slate-50 p-4 rounded-xl font-bold text-xs border outline-none focus:ring-2 focus:ring-primary-500"
            value={form.enroll.subject_id}
            onChange={e => setForm(p => ({ ...p, enroll: { subject_id: e.target.value } }))} required>
            <option value="">Select a subject...</option>
            {data.subjects
              .filter(s => {
                // Respect assigned_staff_ids: empty = visible to all
                const ids = s.assigned_staff_ids || [];
                if (!ids.length || isAdmin) return true;
                return ids.includes(currentProfile?.id);
              })
              .filter(s => !data.enrollments.some(e => e.subject_id === s.id))
              .map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.level})</option>
              ))}
          </select>
          <Button type="submit" className="w-full py-4 rounded-xl font-bold text-[10px]">
            Confirm Enrollment
          </Button>
        </form>
      </Modal>

      {/* Guardian Modal */}
      <Modal isOpen={ui.guardianModal}
        onClose={() => setUi({ ...ui, guardianModal: false, guardianEditId: null })}
        title={ui.guardianEditId ? 'Edit Contact' : 'Add Contact'} className="max-w-md">
        <form onSubmit={handleSaveGuardian} className="p-5 space-y-5">
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 px-1 block">Relationship</label>
            <select className="w-full bg-slate-50 p-4 rounded-xl font-bold text-xs border outline-none focus:ring-2 focus:ring-primary-500"
              value={form.guardian.label}
              onChange={e => setForm(p => ({ ...p, guardian: { ...p.guardian, label: e.target.value } }))}>
              <option value="Mother">Mother</option>
              <option value="Father">Father</option>
              <option value="Guardian">Guardian</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <Input label="Full Name" value={form.guardian.name}
            onChange={e => setForm(p => ({ ...p, guardian: { ...p.guardian, name: e.target.value } }))} required />
          <Input label="Phone Number" value={form.guardian.phone}
            onChange={e => setForm(p => ({ ...p, guardian: { ...p.guardian, phone: e.target.value } }))} required />
          <Input label="Email (Optional)" type="email" value={form.guardian.email}
            onChange={e => setForm(p => ({ ...p, guardian: { ...p.guardian, email: e.target.value } }))} />
          <div className="flex gap-3">
            <Button type="button" variant="ghost"
              onClick={() => setUi({ ...ui, guardianModal: false, guardianEditId: null })} className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-[2] font-bold text-[10px] shadow-lg">Save Contact</Button>
          </div>
        </form>
      </Modal>

      {ui.portfolioOpen && (
        <PortfolioModal
          isOpen={ui.portfolioOpen}
          onClose={() => setUi({ ...ui, portfolioOpen: false })}
          attendanceRecord={ui.selectedAttendance}
          onSave={load}
        />
      )}
    </div>
  );
}